import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Fab, Icon, MenuItem, Tabs, Tab,
  Badge, Card, CardHeader, CardContent, CardActions, IconButton, Paper, Tooltip, withStyles,
} from '@material-ui/core';
import { Select, DateRangePicker } from '..';

const styles = {
  fab: {
    position: 'fixed',
    bottom: '25px',
    right: '15px',
    zIndex: 1,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  dialog: {
    height: '80vh',
  },
  dialogTitle: {
    padding: '0px !important',
  },
  dialogTitleContent: {
    display: 'flex',
  },
  dialogContent: {
    padding: 0,
  },
  tabs: {
    flexGrow: 1,
  },
  textFieldDesktop: {
    flexGrow: 1,
  },
  notesTab: {
    padding: '15px',
    background: '#e0e0e0',
    minHeight: '100%',
  },
  newNoteTab: {
    padding: '15px',
    height: '100%',
    background: '#e0e0e0',
  },
  newNoteTabPaper: {
    padding: '15px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  textArea: {
    height: '100%',
    minHeight: '40px',
    overflow: 'auto',
  },
  inputDiv: {
    height: '100%',
  },
  card: {
    marginBottom: '10px',
  },
  cardHeader: {
    fontSize: '0.75rem',
    color: '#808080',
  },
  cardContent: {
    whiteSpace: 'pre-wrap',
    paddingTop: 0,
    paddingBottom: 0,
  },
  cardActions: {
    padding: '0',
  },
  deleteButton: {
    marginLeft: 'auto',
  },
};

const optionNames = {
  selected: 'Aktuell ausgewählter Zeitraum',
  day: 'Tag',
  week: 'Woche',
  month: 'Monat',
  custom: 'Benutzerdefiniert',
};

class NoteDialog extends Component {
  constructor(props) {
    super(props);

    const { selectedLocation } = props;

    if (selectedLocation) {
      const startDate = moment.unix(props.dateRange.startDate).toISOString();
      const endDate = moment.unix(props.dateRange.endDate).toISOString();
      props.loadNotes({
        entityName: 'location', entityId: selectedLocation.id, startDate, endDate,
      });
    }

    this.state = {
      text: '',
      selectedOption: optionNames.selected,
      options: [],
      selectedTab: 1,
      deleteOpen: false,
      customStart: moment().set('hours', 5).set('minutes', 0).unix(),
      customEnd: moment().add(1, 'days').set('hours', 5).set('minutes', 0)
        .unix(),
    };
  }

  componentDidUpdate(prevProps) {
    const {
      dateRange: oldDateRange, loadNotes, selectedLocation: oldSelectedLocation, open: prevOpen,
    } = prevProps;
    const { dateRange: newDateRange, selectedLocation: newSelectedLocation, open } = this.props;

    const oldLocationId = oldSelectedLocation ? oldSelectedLocation.id : -1;
    const newLocationId = newSelectedLocation ? newSelectedLocation.id : -1;

    if (
      newSelectedLocation && (
        oldDateRange.startDate !== newDateRange.startDate
        || newDateRange.endDate !== oldDateRange.endDate
        || oldLocationId !== newLocationId
      )
    ) {
      const startDate = moment.unix(newDateRange.startDate).toISOString();
      const endDate = moment.unix(newDateRange.endDate).toISOString();

      loadNotes({
        entityName: 'location', entityId: newSelectedLocation.id, startDate, endDate,
      });
    }

    if (open && !prevOpen) {
      this.updateOptions();
    }
  }

  updateOptions = () => {
    const { notes } = this.props;

    const options = this.makeOptions();

    this.setState({
      options,
      selectedTab: notes.length > 0 ? 0 : 1,
    });
  }


  handleClickOpen = () => {
    const { setDialogOpen } = this.props;
    setDialogOpen(true);
  };

  handleClose = () => {
    const { setDialogOpen } = this.props;
    setDialogOpen(false);
  };

  canSubmit = () => {
    const { text } = this.state;
    const { selectedLocation } = this.props;

    return text.trim() !== '' && selectedLocation;
  }

  handleSubmit = () => {
    const {
      text, options, selectedOption, customStart, customEnd,
    } = this.state;
    const {
      createNote, selectedReport, selectedLocation,
    } = this.props;

    const option = options.find(o => o.name === selectedOption);
    const isCustomOption = selectedOption === optionNames.custom;

    const note = {
      entityName: 'location',
      entityId: selectedLocation.id,
      context: JSON.stringify({ reportId: selectedReport.reportID }),
      purpose: 'Reports',
      text: text.trim(),
      validStart: isCustomOption ? moment.unix(customStart).toISOString() : option.validStart,
      validEnd: isCustomOption ? moment.unix(customEnd).toISOString() : option.validEnd,
    };

    createNote(note);
    this.setState({
      selectedTab: 0,
      text: '',
    });
  };

  handleChangeText = (event) => {
    this.setState({
      text: event.target.value,
    });
  }

  handleSelectOption = (event) => {
    this.setState({
      selectedOption: event.target.value,
    });
  }

  makeOptions = () => {
    const { dateRange, selectedLocation } = this.props;
    const { customStart, customEnd } = this.state;

    const startDate = moment.unix(dateRange.startDate);
    const endDate = moment.unix(dateRange.endDate);
    const virtualDayStart = (selectedLocation && selectedLocation.virtualDayStart != null)
      ? moment.duration(selectedLocation.virtualDayStart) : moment.duration(5, 'hours');

    return [
      {
        name: optionNames.selected,
        displayName: `Ausgewählter Zeitraum ${startDate.format('DD.MM.YYYY')} - ${endDate.format('DD.MM.YYYY')}`,
        validStart: startDate.toISOString(),
        validEnd: endDate.toISOString(),
      },
      {
        name: optionNames.day,
        displayName: startDate.format('dd DD.MM.YYYY'),
        validStart: startDate.clone().hour(virtualDayStart.hours()).minute(virtualDayStart.minutes()).toISOString(),
        validEnd: startDate.clone().add(1, 'day').hour(virtualDayStart.hours()).minute(virtualDayStart.minutes())
          .toISOString(),
      },
      {
        name: optionNames.week,
        displayName: `Woche ${startDate.clone().startOf('week').format('DD.MM.YYYY')}
         - ${startDate.clone().endOf('week').format('DD.MM.YYYY')}`,
        validStart: startDate.clone().startOf('week').hour(virtualDayStart.hours()).minute(virtualDayStart.minutes())
          .toISOString(),
        validEnd: startDate.clone().startOf('week').add(1, 'week').hour(virtualDayStart.hours())
          .minute(virtualDayStart.minutes())
          .toISOString(),
      },
      {
        name: optionNames.month,
        displayName: startDate.format('MMMM YYYY'),
        validStart: startDate.clone().startOf('month').hour(virtualDayStart.hours()).minute(virtualDayStart.minutes())
          .toISOString(),
        validEnd: startDate.clone().startOf('month').add(1, 'month').hour(virtualDayStart.hours())
          .minute(virtualDayStart.minutes())
          .toISOString(),
      },
      {
        name: optionNames.custom,
        displayName: 'Benutzerdefiniert',
        validStart: moment.unix(customStart).toISOString(),
        validEnd: moment.unix(customEnd).toISOString(),
      },
    ];
  }

  createCardTitle = (validStart, validEnd) => {
    const start = moment(validStart);
    const end = moment(validEnd);

    if (end.diff(start, 'days', true) === 1) {
      return `${start.format('DD.MM.YYYY')}`;
    }
    if (start.date() === 1 && end.date() === 1 && end.diff(start, 'months') === 1) {
      return `${start.format('MMMM YYYY')}`;
    }

    return `${start.format('DD.MM.YYYY')} - ${end.format('DD.MM.YYYY')}`;
  }

  handleDeleteOpen = () => {
    this.setState({
      deleteOpen: true,
    });
  }

  handleDeleteClose = () => {
    this.setState({
      deleteOpen: false,
    });
  }

  handleDelete = (note) => {
    const { deleteNote } = this.props;

    deleteNote(note);
    this.setState({
      deleteOpen: false,
    });
  }

  renderDeleteNoteDialog = (note) => {
    const { deleteOpen } = this.state;

    return (
      <Dialog
        open={deleteOpen}
        onClose={this.handleDeleteClose}
      >
        <DialogTitle>Notiz löschen</DialogTitle>
        <DialogContent>Wollen Sie diese Notiz wirklich löschen?</DialogContent>
        <DialogActions>
          <Button onClick={this.handleDeleteClose} color="primary">
            Abbrechen
          </Button>
          <Button onClick={() => this.handleDelete(note)} color="primary" autoFocus>
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  handleDatesChanged = ({ startDate, endDate }) => {
    this.setState({
      customStart: startDate,
      customEnd: endDate,
    });
  }

  renderNotes = (index) => {
    const {
      selectedTab,
    } = this.state;
    const { notes } = this.props;

    if (!notes || notes.length < 1) {
      return (
        <div hidden={selectedTab !== index} style={styles.notesTab}>
          Für den ausgewählten Zeitraum gibt es noch keine Notizen.
        </div>
      );
    }

    const noteItems = notes.map(note => (
      <Card key={note.id} style={styles.card}>
        <CardHeader
          title={this.createCardTitle(note.validStart, note.validEnd)}
          titleTypographyProps={{ style: styles.cardHeader }}
        />
        <CardContent style={styles.cardContent}>
          {note.text}
        </CardContent>
        <CardActions style={styles.cardActions}>
          <IconButton style={styles.deleteButton} onClick={this.handleDeleteOpen}>
            <Icon>delete</Icon>
          </IconButton>
          {this.renderDeleteNoteDialog(note)}
        </CardActions>
      </Card>
    ));

    return (
      <div hidden={selectedTab !== index} style={styles.notesTab}>
        {noteItems}
      </div>
    );
  }

  renderNewNote = (index) => {
    const {
      text, selectedOption, options, selectedTab, customStart, customEnd,
    } = this.state;
    const { inApp } = this.props;

    if (selectedTab !== index) {
      return null;
    }

    const optionItems = options.map(option => <MenuItem key={option.name} value={option.name}>{option.displayName}</MenuItem>);
    const showDatePicker = selectedOption === optionNames.custom;

    return (
      <div style={styles.newNoteTab}>
        <Paper style={styles.newNoteTabPaper}>
          <h3>Für Zeitraum:</h3>
          <Select
            value={selectedOption}
            onChange={this.handleSelectOption}
          >
            {optionItems}
          </Select>
          {showDatePicker
          && (
          <DateRangePicker
            startDate={moment.unix(customStart)}
            endDate={moment.unix(customEnd)}
            onDatesChanged={this.handleDatesChanged}
            title="Notizen"
            supressPortal
          />
          )}
          <TextField
            autoFocus
            margin="dense"
            id="name"
            type="text"
            fullWidth
            multiline
            variant="outlined"
            value={text}
            onChange={this.handleChangeText}
            style={inApp ? {} : styles.textFieldDesktop}
            inputProps={{ style: styles.textArea }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{ style: styles.inputDiv }}
          />
          <div style={styles.buttonContainer}>
            <Button onClick={this.handleClose} color="primary">
              Abbrechen
            </Button>
            <Button onClick={this.handleSubmit} color="primary" disabled={!this.canSubmit}>
              Speichern
            </Button>
          </div>
        </Paper>
      </div>
    );
  }

  handleChangeTab = (_, newValue) => {
    this.setState({
      selectedTab: newValue,
    });
  }

  render() {
    const { selectedTab } = this.state;
    const {
      notes, useDesktopLayout, open, classes,
    } = this.props;

    return (
      <div>
        <Tooltip title="Notizen">
          <Fab
            className="bottom-note-fab doNotPrint"
            variant="round"
            color="primary"
            onClick={this.handleClickOpen}
            style={styles.fab}
            size="large"
          >
            <Badge badgeContent={notes.length} color="secondary">
              <Icon>post_add</Icon>
            </Badge>
          </Fab>
        </Tooltip>
        <Dialog
          open={open}
          onClose={this.handleClose}
          fullWidth
          maxWidth="lg"
          fullScreen={!useDesktopLayout}
          PaperProps={useDesktopLayout ? { style: styles.dialog } : { }}
        >
          <DialogTitle classes={{ root: classes.dialogTitle }} id="form-dialog-title">
            <Tabs value={selectedTab} onChange={this.handleChangeTab} variant="fullWidth" style={styles.tabs}>
              <Tab label="Notizen" />
              <Tab label="Neue Notiz" />
            </Tabs>
          </DialogTitle>
          <DialogContent style={styles.dialogContent}>
            {this.renderNotes(0)}
            {this.renderNewNote(1)}
          </DialogContent>
        </Dialog>
      </div>
    );
  }
}

NoteDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  useDesktopLayout: PropTypes.bool,
  selectedReport: PropTypes.object,
  selectedLocation: PropTypes.object,
  dateRange: PropTypes.object,
  notes: PropTypes.array,
  open: PropTypes.bool.isRequired,
  inApp: PropTypes.bool.isRequired,
  createNote: PropTypes.func,
  loadNotes: PropTypes.func,
  deleteNote: PropTypes.func,
  setDialogOpen: PropTypes.func,
};

NoteDialog.defaultProps = {
  useDesktopLayout: true,
  selectedReport: {},
  selectedLocation: {},
  dateRange: {},
  notes: [],
  createNote: () => {},
  loadNotes: () => {},
  deleteNote: () => {},
  setDialogOpen: () => {},
};

export default withStyles(styles)(NoteDialog);
